import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { colors, font } from "../styles/theme";
import useScrollLock from "../hooks/useScrollLock";
import { preloadImages } from "./SmartImage";

// Лайтбокс: галерея фото объекта. photos — массив путей, start — индекс стартового фото.
export default function Lightbox({ photos, title, start = 0, onClose }) {
  const [idx, setIdx] = useState(start);
  const [closing, setClosing] = useState(false);

  useScrollLock(true);

  // Мягкое закрытие: проигрываем fade-out, затем размонтируем.
  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 200);
  }, [onClose]);

  // Предзагружаем соседние кадры, чтобы листание было без лага.
  useEffect(() => {
    const n = photos.length;
    preloadImages([photos[(idx + 1) % n], photos[(idx - 1 + n) % n]]);
  }, [idx, photos]);

  const prev = useCallback(() => setIdx((i) => (i - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % photos.length), [photos.length]);

  // Свайп на тач-устройствах.
  const touchX = useRef(null);
  const onTouchStart = (e) => (touchX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) (dx < 0 ? next : prev)();
    touchX.current = null;
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [handleClose, prev, next]);

  const navBtn = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 52,
    height: 52,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,.25)",
    background: "rgba(0,0,0,.4)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 2,
  };

  return createPortal(
    <div
      onClick={handleClose}
      className={"lightbox-root " + (closing ? "ovl-fade-out" : "ovl-fade-in")}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        zIndex: 1000,
        background: "rgba(8,8,11,.92)",
        display: "flex",
        flexDirection: "column",
        padding: "20px",
      }}
    >
      {/* Верхняя панель */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, color: "#fff" }}>
        <div>
          <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 18, textTransform: "uppercase" }}>{title}</div>
          <div style={{ fontSize: 13, color: "#aab0b8", marginTop: 2 }}>
            {idx + 1} / {photos.length}
          </div>
        </div>
        <button
          onClick={handleClose}
          aria-label="Закрыть"
          style={{ width: 44, height: 44, borderRadius: 10, border: "none", background: "rgba(255,255,255,.1)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          <X size={22} />
        </button>
      </div>

      {/* Основное фото (свайп на тач) */}
      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0, marginTop: 14 }}
      >
        {photos.length > 1 && (
          <button className="lightbox-nav" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Назад" style={{ ...navBtn, left: 6 }}>
            <ChevronLeft size={26} />
          </button>
        )}
        <img
          src={photos[idx]}
          alt={title + " — фото " + (idx + 1)}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }}
        />
        {photos.length > 1 && (
          <button className="lightbox-nav" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Вперёд" style={{ ...navBtn, right: 6 }}>
            <ChevronRight size={26} />
          </button>
        )}
      </div>

      {/* Миниатюры */}
      {photos.length > 1 && (
        <div
          className="lightbox-thumbs"
          onClick={(e) => e.stopPropagation()}
          style={{ display: "flex", gap: 8, marginTop: 14, overflowX: "auto", paddingBottom: 4, justifyContent: "center", flexWrap: "wrap" }}
        >
          {photos.map((src, i) => (
            <button
              key={src}
              onClick={() => setIdx(i)}
              style={{
                flex: "none",
                width: 68,
                height: 50,
                padding: 0,
                border: i === idx ? `2px solid ${colors.red}` : "2px solid transparent",
                borderRadius: 7,
                overflow: "hidden",
                cursor: "pointer",
                background: "none",
                opacity: i === idx ? 1 : 0.6,
              }}
            >
              <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}
